/**
 * localStorage-backed selection hook for {mode, profileA, profileB}.
 *
 * Selection is the 1v1 arena's primary navigation state — changing mode
 * or either slot re-scopes Search and Results. Changing mode clears both
 * slots (profile pools differ per mode). Slot changes preserve mode.
 *
 * `clearIfInvalid(validApiNames)` is called by AppShell once the profile
 * registry loads: if the persisted value no longer matches a known profile
 * (e.g. registry rolled forward, profile removed), the slot is cleared.
 *
 * Implementation: a module-level singleton subscribed via
 * `useSyncExternalStore` so every `useSelection()` call sees the same
 * state. A plain `useState` would give each component its own copy —
 * picking a profile in the sidebar would update AppShell but leave the
 * Search page stale.
 */

import { useCallback, useSyncExternalStore } from "react";

import type { Mode } from "@ranking-arena/shared/types";

export interface Selection {
  mode: Mode;
  profileA: string | null;
  profileB: string | null;
}

const SEL_KEY = "arena.selection.v2";
const DEFAULT: Selection = {
  mode: "doc-search",
  profileA: null,
  profileB: null,
};

function read(): Selection {
  if (typeof localStorage === "undefined") return DEFAULT;
  try {
    const raw = localStorage.getItem(SEL_KEY);
    if (!raw) return DEFAULT;
    const p = JSON.parse(raw) as Partial<Selection>;
    return {
      mode: p.mode === "slide-search" ? "slide-search" : "doc-search",
      profileA: p.profileA ?? null,
      profileB: p.profileB ?? null,
    };
  } catch {
    return DEFAULT;
  }
}

function write(s: Selection): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(SEL_KEY, JSON.stringify(s));
  } catch {
    /* quota or privacy mode — silent */
  }
}

// ---- Module-level store ---------------------------------------------------

let state: Selection = read();
const listeners = new Set<() => void>();

function emit(): void {
  write(state);
  for (const fn of listeners) fn();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): Selection {
  return state;
}

export function setMode(m: Mode): void {
  if (state.mode === m) return;
  state = { mode: m, profileA: null, profileB: null };
  emit();
}

export function setProfileA(p: string | null): void {
  if (state.profileA === p) return;
  state = { ...state, profileA: p };
  emit();
}

export function setProfileB(p: string | null): void {
  if (state.profileB === p) return;
  state = { ...state, profileB: p };
  emit();
}

// Atomic A ↔ B flip. Single emission avoids the transient {A=X, B=X} state
// that two sequential setters would produce, which would break the
// exclude-list invariant in ProfileSelect.
export function swapProfiles(): void {
  if (!state.profileA || !state.profileB) return;
  state = { ...state, profileA: state.profileB, profileB: state.profileA };
  emit();
}

export function clearIfInvalid(validApiNames: ReadonlySet<string>): void {
  const nextA = state.profileA && !validApiNames.has(state.profileA) ? null : state.profileA;
  const nextB = state.profileB && !validApiNames.has(state.profileB) ? null : state.profileB;
  if (nextA === state.profileA && nextB === state.profileB) return;
  state = { ...state, profileA: nextA, profileB: nextB };
  emit();
}

// ---- Hook -----------------------------------------------------------------

export function useSelection() {
  const sel = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  // Wrap the module-level setters in useCallback so referential identity is
  // stable across renders — callers pass these to dependency arrays.
  const _setMode = useCallback(setMode, []);
  const _setProfileA = useCallback(setProfileA, []);
  const _setProfileB = useCallback(setProfileB, []);
  const _swapProfiles = useCallback(swapProfiles, []);
  const _clearIfInvalid = useCallback(clearIfInvalid, []);
  return {
    ...sel,
    setMode: _setMode,
    setProfileA: _setProfileA,
    setProfileB: _setProfileB,
    swapProfiles: _swapProfiles,
    clearIfInvalid: _clearIfInvalid,
  };
}
